// LanguageExposureContext.tsx
"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { PositionList } from "@/data/positions/positions-schema";
import { getPositions } from "@/features/positions/server/actions/getPositions";
import { AddPositionFormData } from "../schemas/addPositionSchema";
import { useProfiles } from "@/features/profiles/context/ProfilesContext";
import { CurrentProfile } from "@/features/pd-builder/types/pd-types";
import { profilesOptions } from "@/data/data";
interface PositionsContextType {
  positions: PositionList[];
  deletePosition: (
    positionId: string,
  ) => Promise<{ error: boolean; message: string }>;
  addPosition: (input: AddPositionFormData) => Promise<any>;
  getPositionsByDepartment: (department: string) => any;
  getReportsToPositions: ({ department, level, defaultValue }: any) => any;
}

function getRandomThreeDigitNumber() {
  return Math.floor(Math.random() * 900) + 100;
}

const PositionsContext = createContext<PositionsContextType | undefined>(
  undefined,
);

export const PositionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [positions, setPositions] = useState<PositionList[]>([]);
  const { getProfile, profiles } = useProfiles();
  const getPositionsData = async () => {
    try {
      const allPositions = await getPositions();
      setPositions(allPositions);
    } catch (error) {
      setPositions([]);
    }
  };

  useEffect(() => {
    getPositionsData();
  }, []);

  const deletePosition = async (positionId: string) => {
    // console.log("trying to delte", positionId);
    return {
      error: false,
      message: "tryig to delete",
    };
  };

  const getPositionsByDepartment = async (department: string) => {
    // console.log("department", department)
    const listOfPostions =
      positions.filter((position) => position.department === department) ??
      positions;
    return listOfPostions.map((position) => {
      return { label: position.title, value: position.title, disable: true };
    });
  };

  const getReportsToPositions = ({ department, level, defaultValue }: any) => {
    // Step 0: Get current profile level
    const currentLevel = profilesOptions.find((p: any) => p.value === level);
    // Step 1: Get list of levels higher than current level and return a set of profileIds
    const profileLevels =
      currentLevel &&
      profilesOptions
        .filter((profile: any) => {
          return profile?.level > currentLevel?.level;
        })
        .map((p) => p?.value);
    const setToKeep = new Set(profileLevels);
    // Step 2: Get list of positions by department
    const listOfPostions =
      positions.filter((position) => position.department === department) ??
      positions;
    // Step 3: Filter list of positions by department to the list of levels
    const positionsToKep = listOfPostions.filter((position: any) =>
      setToKeep.has(position.profileId),
    );
    // Step 4: Add current default value to list of filtered positions
    const finalPositions =
      positionsToKep?.length > 0 ? positionsToKep : listOfPostions;
    // Step 5: Prepare
    const reportsToPositions = finalPositions.map((position) => {
      return {
        label: `${position.positionId}, ${position.title}`,
        value: `${position.positionId}, ${position.title}`,
      };
    });

    const finalArray = [
      ...reportsToPositions,
      { label: defaultValue, value: defaultValue },
    ];
    const uniqueObjects = [
      ...new Map(finalArray.map((item) => [item.label, item])).values(),
    ];

    return uniqueObjects;
  };

  const addPosition = async (input: AddPositionFormData) => {
    // console.log("values", input)

    // First pull data from profile and add to array
    // console.log("input.profileId", input.profileId)
    const profile = await getProfile(input.profileId);

    // console.log("proifle", profile)
    const randomPositionId = getRandomThreeDigitNumber();
    const positionID = `P-X${randomPositionId}`;
    // Need an adapter to take postion and profile data to return a structured data we can use to save to the database.
    const newPosition = {
      positionId: positionID,
      ...profile,
      ...input,
      level: input.profileId,
      updatedAt: new Date(),
      publishedAt: new Date(),
      status: "draft",
    };

    // console.log("positions", positions)
    // Add to position array
    setPositions([...positions, newPosition]);

    // console.log("position", newPosition)
    return newPosition;
  };
  return (
    <PositionsContext.Provider
      value={{
        positions,
        deletePosition,
        addPosition,
        getPositionsByDepartment,
        getReportsToPositions,
      }}
    >
      {/* Dont do this. Send empty and show empty state on component level */}
      {children}
    </PositionsContext.Provider>
  );
};

export const usePositions = () => {
  const context = useContext(PositionsContext);
  if (!context) throw new Error("Must be used within PositionsProvider");
  return context;
};
